import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getClientConfiguration } from "generated/api/client-configuration";
import { customInstance } from "config/mutator";
import type { JsonNode, ThemeAssetDTO } from "generated/schemas";

import { themeKeys } from "hooks/api/query-keys";

import type { APIThemeData } from "containers/admin/clients/client/theme-page/preview/preview.types";
import type { ThemePalette } from "containers/admin/clients/client/theme-configuration-page/theme-configuration.types";
import type { TypekitResponse } from "types";
import type { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";

const clientConfigurationApi = getClientConfiguration();

export interface GoogleFontsResponse {
  items: GoogleFont[];
}

/**
 * Fetches the saved theme configuration for a client. Backed by the generic
 * configuration GET (`get2`), which the backend serves as untyped `JsonNode`.
 */
export async function fetchTheme(clientId: string): Promise<APIThemeData> {
  const data = await clientConfigurationApi.get2({ clientId, config: "theme" });
  return data as unknown as APIThemeData;
}

/** Replaces the whole theme document — the backend keeps nothing that is not in the body. */
async function postTheme<Theme extends SavedTheme>(clientId: string, theme: Theme): Promise<Theme> {
  const data = await clientConfigurationApi.createOrUpdate({ clientId }, theme as unknown as JsonNode);
  toast.success({ data: { title: "Saved theme" } });
  return data as unknown as Theme;
}

/**
 * Creates or updates the theme configuration for a client. Shows a success toast. Keys the legacy
 * page does not know — the Brand identity `palette` — are carried over from the saved document, or
 * a legacy save would delete them.
 */
export async function saveTheme(clientId: string, attributes: APIThemeData): Promise<APIThemeData> {
  const current: SavedTheme = await fetchTheme(clientId);

  return postTheme(clientId, { ...current, ...attributes });
}

/**
 * Fetches Adobe Typekit fonts for a client's project.
 */
export async function fetchTypekitFonts(
  clientId: string,
  projectId: string,
): Promise<TypekitResponse> {
  const data = await clientConfigurationApi.getTypekitJson({ clientId, typekitId: projectId });
  return data as unknown as TypekitResponse;
}

/**
 * Uploads a theme image asset (e.g. banner) for a client. Posts
 * `multipart/form-data` via `customInstance` because the generated `assetUpload`
 * serializes the body as JSON, which the backend does not accept for file uploads.
 */
export async function uploadThemeAsset(clientId: string, file: File): Promise<ThemeAssetDTO> {
  const formData = new FormData();
  formData.append("uploadFile", file);

  try {
    return await customInstance<ThemeAssetDTO>({
      url: `/api/clients/${clientId}/theme-assets`,
      method: "POST",
      params: { type: "image" },
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    });
  } catch (error) {
    console.error("Theme asset upload error:", { error });
    throw error;
  }
}

/**
 * Fetches the list of Google fonts ordered by popularity. Uses `customInstance`
 * directly because the generated `getGoogleFonts` does not expose the `sort` param.
 */
export async function fetchGoogleFonts(): Promise<GoogleFontsResponse> {
  return customInstance<GoogleFontsResponse>({
    url: "/api/fonts",
    method: "GET",
    params: { sort: "popularity" },
  });
}

/** A client that has never saved a theme gets `{}` back, so every section may be missing. */
export type SavedTheme = Partial<APIThemeData> & { palette?: ThemePalette };

/** The sections a save replaces; every other section of the saved document is kept. */
export type ThemeSectionsUpdate = Partial<SavedTheme>;

export const useThemeQuery = (clientId: string) =>
  useQuery({
    queryKey: themeKeys.detail(clientId),
    queryFn: (): Promise<SavedTheme> => fetchTheme(clientId),
    enabled: Boolean(clientId),
  });

/**
 * Saves some sections of the theme. The backend replaces the whole theme document on every POST, so
 * the other sections are re-read right before the write rather than taken from a possibly stale
 * cache — otherwise a save here would roll back a section saved elsewhere since this page loaded.
 */
export const useSaveThemeSections = (clientId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sections: ThemeSectionsUpdate): Promise<SavedTheme> => {
      const current: SavedTheme = await fetchTheme(clientId);

      return postTheme(clientId, { ...current, ...sections });
    },
    onSuccess: (savedTheme) => {
      queryClient.setQueryData(themeKeys.detail(clientId), savedTheme);
    },
  });
};

export const useUploadThemeAsset = (clientId: string) =>
  useMutation({ mutationFn: (file: File) => uploadThemeAsset(clientId, file) });

export const useGoogleFontsQuery = () =>
  useQuery({
    queryKey: themeKeys.googleFonts(),
    queryFn: fetchGoogleFonts,
    select: (response) => response.items,
    staleTime: Infinity,
  });

/** Shared by the preview query and the imperative check run when a Project ID is applied. */
export const typekitKitQueryOptions = (clientId: string, projectId: string) =>
  queryOptions({
    queryKey: themeKeys.typekitKit(clientId, projectId),
    queryFn: () => fetchTypekitFonts(clientId, projectId),
    staleTime: Infinity,
    retry: false,
  });

export const useTypekitKitQuery = (clientId: string, projectId: string) =>
  useQuery({ ...typekitKitQueryOptions(clientId, projectId), enabled: Boolean(clientId && projectId) });
